<template>
  <div class="page-container">
    <el-card shadow="never" class="page-card">
      <div class="toolbar">
        <div>
          <h3 class="toolbar-title">展示图管理</h3>
          <p class="toolbar-desc">
            开屏图建议 <b>9:16</b>（如 1080×1920）。<br>
            邀请海报请上传 <b>3:4</b>（如  1080×1440）。
          </p>
        </div>
        <el-button icon="el-icon-refresh" @click="fetchList">刷新</el-button>
      </div>

      <el-tabs v-model="activeType" @tab-click="onTabClick">
        <el-tab-pane label="开屏主图" name="splash" />
        <el-tab-pane label="邀请海报" name="poster" />
      </el-tabs>

      <div class="filter-bar">
        <div class="filter-group">
          <el-select v-model="query.status" placeholder="全部状态" clearable class="filter-select">
            <el-option label="启用" :value="1" />
            <el-option label="停用" :value="0" />
          </el-select>
          <el-button type="primary" icon="el-icon-search" @click="fetchList">查询</el-button>
        </div>
        <el-button type="primary" icon="el-icon-plus" @click="openAdd">
          {{ activeType === 'splash' ? '上传开屏图' : '上传海报图' }}
        </el-button>
      </div>

      <div v-if="error" class="error">{{ error }}</div>

      <el-table
        v-else
        :data="tableData"
        border
        stripe
        v-loading="loading"
        element-loading-text="加载中..."
        row-key="id"
        empty-text="暂无图片，请点击上传"
      >
        <el-table-column prop="id" label="ID" width="72" align="center" />
        <el-table-column label="预览" width="96" align="center">
          <template slot-scope="scope">
            <el-image
              v-if="scope.row.imageUrl"
              :key="scope.row.id + '-' + scope.row.imageUrl"
              :src="scope.row.imageUrl"
              fit="cover"
              class="table-thumb"
              :preview-src-list="[scope.row.imageUrl]"
            >
              <div slot="placeholder" class="img-placeholder"><i class="el-icon-loading"></i></div>
              <div slot="error" class="img-error"><i class="el-icon-picture-outline"></i></div>
            </el-image>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="排序" width="88" align="center">
          <template slot-scope="scope">{{ scope.row.sortOrder != null ? scope.row.sortOrder : 0 }}</template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.remark || '-' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="88" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status === 1" type="success" size="small">启用</el-tag>
            <el-tag v-else type="info" size="small">停用</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" width="168" align="center">
          <template slot-scope="scope">{{ formatDate(scope.row.updatedTime || scope.row.createdTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="openEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      :title="dialogMode === 'add' ? '上传图片' : '编辑图片'"
      :visible.sync="dialogVisible"
      width="480px"
      center
      append-to-body
      :close-on-click-modal="false"
      @close="resetForm"
    >
      <el-form ref="imgForm" :model="form" :rules="rules" label-width="88px">
        <el-form-item label="类型">
          <el-tag size="small">{{ form.imageType === 'splash' ? '开屏主图' : '邀请海报' }}</el-tag>
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number
            v-model="form.sortOrder"
            :min="0"
            :max="999999"
            :step="1"
            controls-position="right"
            style="width:100%;"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model.trim="form.remark" maxlength="64" show-word-limit placeholder="可选，方便后台识别" />
        </el-form-item>
        <el-form-item label="图片" prop="imageUrl">
          <div class="upload-row">
            <el-upload
              class="image-uploader"
              action="#"
              :auto-upload="false"
              :show-file-list="false"
              accept="image/*"
              :on-change="onFileChange"
              :before-upload="beforeImageUpload"
            >
              <img v-if="displayImageUrl" :src="displayImageUrl" class="image-uploader-thumb" alt="" />
              <i v-else class="el-icon-plus image-uploader-icon" />
            </el-upload>
          </div>
          <p class="upload-tip">{{ form.imageType === 'poster' ? '请上传 3:4 竖图，如 1080×1440。jpg / png / webp，最大 10MB。' : '请上传 9:16 竖图，如 1080×1920。jpg / png / webp，最大 10MB。' }}</p>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitForm">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { get, post, put, del, uploadShopImage } from '@/utils/http'

const API = {
  list: '/admin/display-image/list',
  add: '/admin/display-image/add',
  update: '/admin/display-image/update',
  remove: id => `/admin/display-image/${id}`,
  upload: '/admin/display-image/upload-image'
}

export default {
  name: 'DisplayImageManagement',
  data() {
    return {
      loading: false,
      error: '',
      activeType: 'splash',
      tableData: [],
      query: {
        status: null
      },
      dialogVisible: false,
      dialogMode: 'add',
      submitLoading: false,
      pendingFile: null,
      previewUrl: '',
      form: {
        id: null,
        imageType: 'splash',
        imageUrl: '',
        sortOrder: 0,
        status: 1,
        remark: ''
      },
      rules: {
        sortOrder: [{ required: true, message: '请输入排序', trigger: 'change' }]
      }
    }
  },
  computed: {
    displayImageUrl() {
      return this.previewUrl || this.form.imageUrl
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    onTabClick() {
      this.query.status = null
      this.fetchList()
    },
    beforeImageUpload(file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
      if (!file.type || !allowedTypes.includes(file.type.toLowerCase())) {
        this.$message.error('仅支持 jpg、png、gif、webp')
        return false
      }
      const fileName = (file.name || '').toLowerCase()
      if (!allowedExtensions.some(ext => fileName.endsWith(ext))) {
        this.$message.error('文件扩展名不正确')
        return false
      }
      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('图片大小不能超过 10MB')
        return false
      }
      return true
    },
    onFileChange(file) {
      this.pendingFile = file.raw
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.previewUrl = URL.createObjectURL(file.raw)
    },
    clearPreview() {
      this.pendingFile = null
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = ''
      }
    },
    async fetchList() {
      this.loading = true
      this.error = ''
      try {
        const params = { type: this.activeType }
        if (this.query.status != null && this.query.status !== '') {
          params.status = this.query.status
        }
        const res = await get(API.list, params)
        this.tableData = Array.isArray(res.data) ? res.data : []
      } catch (e) {
        this.tableData = []
        this.error = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    openAdd() {
      this.dialogMode = 'add'
      this.form = {
        id: null,
        imageType: this.activeType,
        imageUrl: '',
        sortOrder: this.tableData.length,
        status: 1,
        remark: this.activeType === 'splash' ? '开屏主图' : '邀请海报'
      }
      this.clearPreview()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.imgForm && this.$refs.imgForm.clearValidate())
    },
    openEdit(row) {
      this.dialogMode = 'edit'
      this.clearPreview()
      this.form = {
        id: row.id,
        imageType: row.imageType || this.activeType,
        imageUrl: row.imageUrl || '',
        sortOrder: row.sortOrder != null ? row.sortOrder : 0,
        status: row.status === 0 ? 0 : 1,
        remark: row.remark || ''
      }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.imgForm && this.$refs.imgForm.clearValidate())
    },
    resetForm() {
      this.clearPreview()
    },
    submitForm() {
      this.$refs.imgForm.validate(async valid => {
        if (!valid) return
        if (this.dialogMode === 'add' && !this.pendingFile && !this.form.imageUrl) {
          this.$message.error('请选择图片')
          return
        }
        this.submitLoading = true
        try {
          if (this.pendingFile) {
            const url = await uploadShopImage(API.upload, this.pendingFile)
            this.form.imageUrl = url
            this.pendingFile = null
            if (this.previewUrl) {
              URL.revokeObjectURL(this.previewUrl)
              this.previewUrl = ''
            }
          }
          const payload = {
            imageType: this.form.imageType,
            imageUrl: this.form.imageUrl,
            sortOrder: this.form.sortOrder,
            status: this.form.status,
            remark: this.form.remark
          }
          if (this.dialogMode === 'add') {
            await post(API.add, payload)
            this.$message.success('上传成功')
          } else {
            payload.id = this.form.id
            await put(API.update, payload)
            this.$message.success('保存成功')
          }
          this.dialogVisible = false
          await this.fetchList()
        } catch (e) {
          this.$message.error(e.message || '提交失败')
        } finally {
          this.submitLoading = false
        }
      })
    },
    handleDelete(row) {
      this.$confirm('确定删除这张图片？删除后 App 将不再展示。', '提示', { type: 'warning' })
        .then(async () => {
          await del(API.remove(row.id))
          this.$message.success('已删除')
          await this.fetchList()
        })
        .catch(() => {})
    },
    formatDate(val) {
      if (!val) return '-'
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return String(val)
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 16px;
}
.page-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}
.toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.toolbar-title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 600;
}
.toolbar-desc {
  margin: 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: #fafafa;
  border-radius: 6px;
  margin-bottom: 16px;
}
.filter-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.filter-select {
  width: 140px;
}
.error {
  color: #f56c6c;
  padding: 16px;
  text-align: center;
}
.table-thumb {
  width: 56px;
  height: 56px;
  border-radius: 6px;
}
.upload-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.upload-tip {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
}
.image-uploader ::v-deep .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
}
.image-uploader ::v-deep .el-upload:hover {
  border-color: #409eff;
}
.image-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 120px;
  height: 120px;
  line-height: 120px;
  text-align: center;
  display: block;
}
.image-uploader-thumb {
  width: 120px;
  height: 120px;
  display: block;
  object-fit: cover;
}
.img-placeholder,
.img-error {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  border-radius: 6px;
  color: #dcdfe6;
  font-size: 18px;
}
</style>
